import React, { useState } from "react";
import { createPortal } from "react-dom";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import MoneyInput from "../MoneyInput";
import { EmployeePicker } from "./WhtPickers";
import { COMP_FIELDS, fieldEnabledFor } from "./compFields";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import ConfirmModal from "../ConfirmModal";
import { MissingAccountBanner } from "./glUi";
import { pesosToCentavos } from "../../utils/whtMoney";

// Edit one saved compensation record (PUT). The server re-validates; field
// errors come back in the batch shape with line 1.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const inputClass =
  "w-full border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500";

// The stored snapshot is enough to show the employee until another is picked.
export const employeeFromRecord = (r) => r.employee && {
  _id: r.employee,
  lastName: r.employeeLastName,
  firstName: r.employeeFirstName,
  middleName: r.employeeMiddleName || "",
  tin: r.employeeTin || null,
  isMwe: Boolean(r.employeeIsMwe),
};

const CompRecordEditModal = ({ client, record, onClose, onSaved, onOpenPostingAccounts }) => {
  const [form, setForm] = useState(() => ({
    month: record.month,
    description: record.description || "",
    employee: employeeFromRecord(record),
    otherNonTaxableSpecify: record.otherNonTaxableSpecify || "",
    ...Object.fromEntries(COMP_FIELDS.map((f) => [f.key, (record[f.key] || 0) / 100])),
  }));
  const [errors, setErrors] = useState({});
  const [confirmRepost, setConfirmRepost] = useState(false);
  const [missingAccount, setMissingAccount] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const pickEmployee = (employee) => setForm((f) => {
    const next = { ...f, employee };
    if (employee) for (const field of COMP_FIELDS) if (!fieldEnabledFor(field, employee)) next[field.key] = 0;
    return next;
  });

  const save = async () => {
    setErrors({});
    setMissingAccount(null);
    setConfirmRepost(false);
    try {
      const res = await withholdingTaxAPI.updateCompRecord(client._id, record._id, {
        month: Number(form.month),
        description: form.description.trim(),
        employee: form.employee?._id,
        otherNonTaxableSpecify: form.otherNonTaxableSpecify.trim(),
        ...Object.fromEntries(COMP_FIELDS.map((f) => [f.key, pesosToCentavos(form[f.key]) || 0])),
      });
      notify.success("Saved", "The compensation record was updated.");
      await onSaved(res.data, res.warnings || []);
    } catch (e) {
      if (e.lineErrors?.length) setErrors(e.lineErrors[0].fields || {});
      if (e.code === "POSTING_ACCOUNT_MISSING") setMissingAccount(e);
      notify.error("Couldn't save", e.message || "Try again.");
    }
  };
  const { mutate: runSave, isPending: saving } = useGuardedMutation(save);

  const err = (field) => errors[field] && <p role="alert" className="text-xs text-danger-500 mt-0.5">{errors[field]}</p>;

  return createPortal(
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-[9999] p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="comp-edit-title" className="bg-white rounded-xl border border-ink-line shadow-card-hover p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h2 id="comp-edit-title" className="text-lg font-medium text-ink">Edit compensation record</h2>
        {record.isPostedToGL && <p className="text-xs text-ink-body mt-1">Posted to the GL — saving posts a reversal and a new entry.</p>}
        {missingAccount && <div className="mt-3"><MissingAccountBanner error={missingAccount} onOpenPostingAccounts={onOpenPostingAccounts} /></div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <label className="text-xs font-medium text-ink-body">Month
            <select value={form.month} onChange={(e) => set({ month: Number(e.target.value) })} className={`${inputClass} mt-1`}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            {err("month")}
          </label>
          <div className="text-xs font-medium text-ink-body">Employee
            <div className="mt-1"><EmployeePicker clientId={client._id} value={form.employee} onChange={pickEmployee} ariaLabel="Employee" /></div>
            {err("employee")}
          </div>
          <label className="text-xs font-medium text-ink-body">Description
            <input value={form.description} onChange={(e) => set({ description: e.target.value })} className={`${inputClass} mt-1`} />
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mt-4">
          {COMP_FIELDS.map((f) => {
            const enabled = fieldEnabledFor(f, form.employee);
            return (
              <div key={f.key} className="grid grid-cols-[2.5rem_1fr_9rem] items-center gap-2">
                <span className="text-xs font-medium text-primary-700 bg-primary-50 rounded-md px-2 py-0.5 text-center">{f.item}</span>
                <label htmlFor={`edit-${f.key}`} className={`text-xs ${enabled ? "text-ink-body" : "text-ink-line"}`}>{f.label}</label>
                <div>
                  <MoneyInput id={`edit-${f.key}`} value={form[f.key]} onValueChange={(v) => set({ [f.key]: v })} disabled={!enabled} className="w-full" />
                  {err(f.key)}
                </div>
              </div>
            );
          })}
          <label className="md:col-span-2 text-xs text-ink-body">Item 20 — specify
            <input aria-label="Other non-taxable compensation (specify)" value={form.otherNonTaxableSpecify}
              onChange={(e) => set({ otherNonTaxableSpecify: e.target.value })} className={`${inputClass} mt-1`} />
            {err("otherNonTaxableSpecify")}
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          {/* A posted row: saving reverses its entry and posts a new one (G1). */}
          <Button onClick={() => (record.isPostedToGL ? setConfirmRepost(true) : runSave())} loading={saving}>Save changes</Button>
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmRepost}
        title="Edit a posted record?"
        message="This will post a reversal and a new entry."
        confirmLabel="Save and repost"
        variant="primary"
        loading={saving}
        onConfirm={() => runSave()}
        onCancel={() => setConfirmRepost(false)}
      />
    </div>,
    document.body
  );
};

export default CompRecordEditModal;
